import { useEffect } from 'react';

import { useBrowserDefaults } from '@/hooks/useBrowserDefaults';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

const AUTO_DETECT_SESSION_KEY = 'settings_auto_detected';

export function AutoDetectSettings() {
  const user = useAuthStore(state => state.user);
  const api = useTypedApi();
  const browserDefaults = useBrowserDefaults();

  useEffect(() => {
    // Only run if user is logged in
    if (!user) return;

    // Wait for browser detection to complete (currency starts as empty string)
    if (!browserDefaults.currency) return;

    // Check if we've already auto-detected in this session
    const hasAutoDetected = sessionStorage.getItem(AUTO_DETECT_SESSION_KEY);
    if (hasAutoDetected) return;

    // Mark as processing immediately to prevent concurrent runs
    sessionStorage.setItem(AUTO_DETECT_SESSION_KEY, 'processing');

    const checkAndUpdateSettings = async () => {
      try {
        // Get current user settings
        const response = await api.user.getSettings();
        const currentSettings = response.data as {
          currency?: string;
          language?: string;
        };

        // Check if any settings are missing
        const needsUpdate =
          !currentSettings?.currency || !currentSettings?.language;

        if (!needsUpdate) {
          // Mark as checked even if no update needed
          sessionStorage.setItem(AUTO_DETECT_SESSION_KEY, 'true');
          return;
        }

        // Prepare update with detected values for missing fields
        const updates: {
          currency?: string;
          language?: string;
        } = {};

        if (!currentSettings?.currency && browserDefaults.currency) {
          updates.currency = browserDefaults.currency;
        }

        if (!currentSettings?.language && browserDefaults.language) {
          updates.language = browserDefaults.language;
        }

        // Only update if we have something to update
        if (Object.keys(updates).length > 0) {
          await api.user.updateSettings(updates);
        }

        // Mark as completed after successful check (whether updated or not)
        sessionStorage.setItem(AUTO_DETECT_SESSION_KEY, 'true');
      } catch {
        // Remove the processing flag on error, so it can retry next time
        sessionStorage.removeItem(AUTO_DETECT_SESSION_KEY);
      }
    };

    // Add a small delay to ensure browser APIs are ready
    const timer = setTimeout(checkAndUpdateSettings, 1000);

    return () => {
      clearTimeout(timer);
      // If component unmounts while processing, remove the flag
      const currentValue = sessionStorage.getItem(AUTO_DETECT_SESSION_KEY);
      if (currentValue === 'processing') {
        sessionStorage.removeItem(AUTO_DETECT_SESSION_KEY);
      }
    };
  }, [user, browserDefaults, api]); // Include browserDefaults to get updated values after detection

  return null; // This component doesn't render anything
}
